/**
 * PermisosAgentesPanel — GEN-TL-008: vista agente x herramienta sobre las fichas
 * (el mismo dato que aplica el kernel en cada turno) y vía PROVISIONAL del
 * administrador para asignar herramientas: casillas + «para qué» obligatorio.
 * Vale desde el siguiente turno del agente. La asignación por Anne llega en GEN-TL-013.
 * Solo admin: el ítem lo proyecta el kernel y la puerta real es el kernel (403).
 */

import { useEffect, useState } from 'react'
import { asignarHerramientas, getPermisosAgentes } from '../api/client.js'

function cuandoLegible(iso) {
  const t = new Date(iso)
  return Number.isNaN(t.getTime()) ? '—' : t.toLocaleString('es-ES')
}

function Asignar({ agente, catalogo, onHecho, onCancelar }) {
  const [marcadas, setMarcadas] = useState(() => new Set(agente.herramientas.map(h => h.recurso)))
  const [paraQue, setParaQue] = useState('')
  const [error, setError] = useState(null)
  const [guardando, setGuardando] = useState(false)

  function alternar(recurso) {
    setMarcadas(prev => {
      const n = new Set(prev)
      if (n.has(recurso)) n.delete(recurso); else n.add(recurso)
      return n
    })
  }

  async function guardar(e) {
    e.preventDefault()
    if (!paraQue.trim()) { setError('Indica para qué se asignan estas herramientas.'); return }
    setGuardando(true)
    setError(null)
    try {
      await asignarHerramientas(agente.id, { herramientas: [...marcadas], para_que: paraQue.trim() })
      onHecho()
    } catch (err) {
      setError(err?.message || 'No se han podido asignar las herramientas.')
      setGuardando(false)
    }
  }

  return (
    <form onSubmit={guardar} aria-label={`Asignar herramientas a ${agente.nombre}`}>
      <fieldset>
        <legend>Herramientas de la ficha de {agente.nombre}</legend>
        {catalogo.map(c => (
          <label key={c.recurso} style={{ display: 'block' }}>
            <input type="checkbox" checked={marcadas.has(c.recurso)} onChange={() => alternar(c.recurso)} />{' '}
            {c.nombre}
          </label>
        ))}
      </fieldset>
      <label>Para qué{' '}
        <input value={paraQue} onChange={e => setParaQue(e.target.value)} required aria-required="true" />
      </label>
      {error && <p className="equipo-estado" role="alert">{error}</p>}
      <button type="submit" disabled={guardando}>Guardar</button>
      <button type="button" onClick={onCancelar} disabled={guardando}>Cancelar</button>
    </form>
  )
}

export default function PermisosAgentesPanel() {
  const [datos, setDatos] = useState(null)
  const [editando, setEditando] = useState(null)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    let vivo = true
    getPermisosAgentes()
      .then(d => vivo && setDatos(d))
      .catch(() => vivo && setDatos({ error: 'No se han podido cargar los permisos.' }))
    return () => { vivo = false }
  }, [version])

  const agentes = datos?.agentes ?? []
  const catalogo = datos?.catalogo ?? []
  return (
    <section className="mec-visor">
      <h2>Permisos de los agentes</h2>
      <p className="equipo-estado">Cada agente puede usar exactamente las herramientas de su ficha.</p>
      {!datos && <p className="equipo-estado">Cargando…</p>}
      {datos?.error && <p className="equipo-estado" role="alert">{datos.error}</p>}
      {datos && !datos.error && agentes.length === 0 && (
        <p className="equipo-estado">No hay agentes activos.</p>
      )}
      {agentes.length > 0 && (
        <table className="mec-tabla">
          <thead>
            <tr><th>Agente</th><th>Herramientas</th><th>Concedido</th><th /></tr>
          </thead>
          <tbody>
            {agentes.map(a => (
              <tr key={a.id}>
                <td>{a.nombre}</td>
                <td>
                  {a.herramientas.length === 0 ? 'Sin herramientas' : (
                    <ul>
                      {a.herramientas.map(h => (
                        <li key={h.recurso}><strong>{h.nombre}</strong> — {h.descripcion}</li>
                      ))}
                    </ul>
                  )}
                </td>
                <td>
                  {a.asignacion
                    ? <>{a.asignacion.para_que}<br />{a.asignacion.por?.nombre} · {cuandoLegible(a.asignacion.cuando)}</>
                    : '—'}
                </td>
                <td>
                  {editando === a.id ? (
                    <Asignar
                      agente={a}
                      catalogo={catalogo}
                      onHecho={() => { setEditando(null); setVersion(v => v + 1) }}
                      onCancelar={() => setEditando(null)}
                    />
                  ) : (
                    <button type="button" onClick={() => setEditando(a.id)}>Asignar herramientas</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
